import React, { useEffect, useMemo } from 'react';

import { openBlob } from '@apis/common';
import { customsRequestApis } from '@apis/next/customsRequestApis';
import { pdfApis } from '@apis/next/pdfApis';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { QueryContainer } from '@components/QueryContainer';
import { customsRequestStrings } from '@data/customsRequest';
import { secureDataStrings } from '@data/secureData';
import { useSearch } from '@hooks/next/useSearch';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMutation } from '@hooks/useMutation';
import { CustomsRequestHelper } from '@models/next/CustomsRequest';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional, toJST } from '@utils/types';
import clsx from 'clsx';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: `${customsRequestStrings.customsRequest}`,
    acceptPdfPrompt: '通関書類をダウンロードしてください。',
    acceptedMessage: '当通関案件を受け取りました。',
    confirmPrompt: '当案件を受け取りますか？',
    downloadFiles: '通関書類を一括ダウンロード',
    accepted: '受け取りました',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    ...common.flexCenter,
    margin: 16,
    marginBottom: 200,
  },
  operationPanel: {
    ...common.flexColumnCenter,
    ...common.middleText,
    position: 'fixed',
    bottom: 0,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',

    width: '100%',
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
  },
  operationBox: {
    ...common.flexInline,
    ...common.middleText,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',
    maxWidth: 750,
    width: '100%',
  },
  info: {
    ...common.flexColumn,
    ...common.smallerText,
    flex: 1,
    rowGap: 4,
  },
  operation: {
    ...common.flexCenter,
    columnGap: 15,
  },
}));

const searchType = z.object({
  customs_request: z.string(),
  complete_customs_request_access: z.string(),
  complete: z.boolean().optional(),
});

export const CustomsRequestDownloadPdf: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  useEffect(() => {
    document.title = strings.title;
  }, []);

  const { search, updateSearch } = useSearch(searchType);

  const customsRequestQuery = customsRequestApis.useGet(
    '/customs-request/secure-data/:secure_data_key',
    {
      params: {
        secure_data_key: search.customs_request,
      },
    },
  );

  const customsRequestHelper = useMemo(
    () =>
      mapOptional(
        customsRequestQuery.data?.state === 'ACTIVE' ? customsRequestQuery.data.data : undefined,
        (d) => new CustomsRequestHelper(d),
      ),
    [customsRequestQuery.data],
  );

  const pdfQuery = pdfApis.useGet(
    '/pdf/:secure_data_key',
    {
      params: {
        secure_data_key: search.customs_request,
      },
    },
    {
      staleTime: Infinity,
    },
  );

  const downloadFiles = useMutation(() =>
    customsRequestApis.client.get('/customs-request/download-zip/:secure_data_key', {
      params: {
        secure_data_key: search.customs_request,
      },
    }),
  );

  const customsRequestUrl = useMemo(() => {
    if (pdfQuery.data) {
      return URL.createObjectURL(pdfQuery.data);
    }
  }, [pdfQuery.data]);

  const completeCustomsRequest = customsRequestApis.usePut(
    '/customs-request/complete/:secure_data_key',
    {
      params: {
        secure_data_key: search.complete_customs_request_access,
      },
    },
  );

  const confirmCompleteDialog = useConfirmDialog();

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [downloadFiles, completeCustomsRequest],
    formAlertProps: { gutter: false },
  });

  const renderOperationPanel = () => {
    return (
      <div className={classes.operationPanel}>
        {renderAlertsAndSpinnerModal()}
        {(() => {
          if (customsRequestQuery.data?.state === 'ACTIVE') {
            const { data } = customsRequestQuery.data;

            return (
              <div className={classes.operationBox}>
                <div className={classes.info}>
                  <div
                    className={clsx(classes.textPrimary, classes.fontWeight600, classes.middleText)}
                  >
                    {search.complete ? strings.acceptedMessage : strings.acceptPdfPrompt}
                  </div>

                  <div>
                    {customsRequestStrings.createdAt}
                    {': '}
                    {data ? toJST(new Date(data.created_at)) : '-'}
                  </div>

                  <div>
                    {customsRequestStrings.creator}:{' '}
                    {mapOptional(
                      data?.creator,
                      (u) =>
                        (
                          <>
                            {new UserHelper(u).fullname}{' '}
                            <a className={classes.link} target="_blank" href={`mailto:${u.email}`}>
                              {u.email}
                            </a>
                          </>
                        ) ?? '-',
                    )}
                  </div>

                  <div>
                    {customsRequestStrings.submittedAt}:{' '}
                    {mapOptional(customsRequestHelper?.getSubmittedAt(), (d) =>
                      toJST(new Date(d)),
                    ) ?? '-'}
                  </div>

                  <div>
                    {customsRequestStrings.submissionTarget}:{' '}
                    {data?.submission_target?.company ?? '-'}
                  </div>

                  <div>
                    <a
                      href={
                        url('/external/PrintCustomsRequestLog') +
                        '?' +
                        new URLSearchParams({ customs_request: search.customs_request })
                      }
                      target="_blank"
                      className={classes.link}
                    >
                      {customsRequestStrings.changeLog}
                    </a>
                  </div>
                </div>

                <div className={classes.operation}>
                  <Button
                    color="primary"
                    text={strings.downloadFiles}
                    type="button"
                    shape="round"
                    size="small"
                    disabled={!pdfQuery.isSuccess}
                    onClick={async () => {
                      if (!search.complete) {
                        const confirmResult = await confirmCompleteDialog.confirm({
                          title: formStrings.confirm,
                          message: strings.confirmPrompt,
                        });

                        if (confirmResult === 'confirmed') {
                          updateSearch({ complete: true });
                          await completeCustomsRequest.mutateAsync(undefined);
                        }
                      }

                      const files = await downloadFiles.mutateAsync();

                      openBlob(files, 'customs-request-files.zip', 'application/zip');
                    }}
                  />
                </div>
              </div>
            );
          } else if (customsRequestQuery.data?.state === 'CANCELLED') {
            return (
              <Alert open severity="error">
                {secureDataStrings.errorCancelled}
              </Alert>
            );
          } else if (customsRequestQuery.data?.state === 'OUTDATED') {
            return (
              <Alert open severity="warning">
                {secureDataStrings.errorOutdated}
              </Alert>
            );
          }
        })()}
      </div>
    );
  };

  return (
    <QueryContainer queries={[customsRequestQuery, pdfQuery]}>
      <div className={classes.container}>
        <Document key={customsRequestUrl} file={customsRequestUrl} loading="">
          <Page pageNumber={1} width={900} />
          <Page pageNumber={2} width={900} />
        </Document>
      </div>

      {renderOperationPanel()}
      {confirmCompleteDialog.renderDialog()}
    </QueryContainer>
  );
};
